import React from 'react';
import Head from 'next/head';
import Script from 'next/script';
import * as Sentry from '@sentry/nextjs';
import {
  ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import createEmotionCache from '../src/createEmotionCache';
import gql from 'graphql-tag';
import { SessionProvider } from 'context/session/session';
import omitTypename from 'utils/omitTypename';
import { SnackbarProvider } from 'notistack';
import theme from '../src/theme';
import ChatbotWidget from 'components/chatbot/ChatbotWidget';
import ErrorBoundary from 'components/errors/ErrorBoundary';
import type { EmotionCache } from '@emotion/cache';
import type { AppProps, AppContext } from 'next/app';
import { useRouter } from 'next/router';
import { ApolloProvider } from '@apollo/client/react';
import { initApolloClient } from 'hoc/withApollo';

const HIDE_CHATBOT_ROUTES = ['/visite-flash-warming-stripes'];
const HIDE_CHATBOT_PREFIXES = ['/embed/'];

import './styles.css';
import '../containers/layouts/agendaPage/DateFilter.css';
import 'leaflet/dist/leaflet.css';
import 'react-google-places-autocomplete/dist/index.min.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
//import 'react-leaflet-markercluster/dist/styles.min.css';
import 'moment/locale/fr';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

type UserData = {
  id: string;
  surname: string;
  lastname: string;
  email: string;
  role: string;
  phone: string;
  address: string;
  postCode: string;
  city: string;
  openDataPromptSeenAt?: string | null;
};

type MyAppProps = AppProps & {
  emotionCache?: EmotionCache;
  user?: UserData | null;
};

const clientSideEmotionCache = createEmotionCache();

const ISLOGGED = gql`
  query isLogged {
    isLogged {
      id
      surname
      lastname
      email
      role
      phone
      address
      postCode
      city
      openDataPromptSeenAt
    }
  }
`;

const MyApp = (props: MyAppProps) => {
  const { Component, emotionCache = clientSideEmotionCache, pageProps, user } = props;
  // Client partagé par toutes les pages — évite l'erreur "No ApolloProvider" sur les pages statiques.
  const apolloClient = React.useMemo(() => initApolloClient({}), []);
  const router = useRouter();
  const hideChatbot = HIDE_CHATBOT_ROUTES.includes(router.pathname)
    || HIDE_CHATBOT_PREFIXES.some((prefix) => router.pathname.startsWith(prefix));

  React.useEffect(() => {
    console.log(`Disable SEO: ${process.env.NEXT_PUBLIC_SEO_DISABLED}`);
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator) || process.env.NODE_ENV !== 'production') {
      return undefined;
    }
    // Les bots ne sont pas des utilisateurs réels et déclenchent souvent des fetch échoués bruyants.
    const ua = navigator.userAgent || '';
    if (/bot|crawler|spider|googlebot|bingbot|semrush|ahrefs|lighthouse|headlesschrome/i.test(ua)) {
      return undefined;
    }

    let cancelled = false;

    const registerServiceWorker = async () => {
      try {
        // Some deployments disable PWA generation; avoid noisy 404 on /sw.js.
        const swCheck = await fetch('/sw.js', { method: 'HEAD', cache: 'no-store' });
        if (!swCheck.ok) {
          if (!cancelled) {
            console.warn('[Push] Service worker not found (/sw.js), skipping registration.');
          }
          return;
        }

        const registration = await navigator.serviceWorker.register('/sw.js');
        if (!cancelled) {
          console.log('[Push] Service worker registered:', registration.scope);
        }
      } catch (error) {
        // Échec attendu hors-ligne / page déchargée / extension qui bloque le fetch.
        // On log en warn pour ne pas polluer Sentry avec ces erreurs non-actionnables.
        if (!cancelled) {
          console.warn('[Push] Service worker registration failed:', error);
        }
      }
    };

    registerServiceWorker();

    return () => {
      cancelled = true;
    };
  }, []);

  return <ApolloProvider client={apolloClient}><CacheProvider value={emotionCache}>
    <Head>
      <meta charSet="utf-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <title>OUAAA! : Agir pour la Transition Ecologique & Sociale en Aunis | La Rochelle</title>
      {(process.env.NEXT_PUBLIC_SEO_DISABLED && process.env.NEXT_PUBLIC_SEO_DISABLED.localeCompare('true') === 0) && (
        <meta name="robots" content="noindex" />
      )}

      <link rel="manifest" href="/manifest.json" />
      <link rel="icon" href="/favicon-32x32.png" sizes="32x32"></link>
      <link rel="icon" href="/favicon-192x192.png" sizes="192x192"></link>
      <link rel="apple-touch-icon" href="/apple-touch-icon.png"></link>

      <meta name="theme-color" content="#0b8253" />

      <meta key="og:type" property="og:type" content="website" />
      <meta key="og:title" property="og:title" content="OUAAA!" />
      <meta key="og:description" property="og:description" content="OUAAA! : Agir pour la Transition Ecologique & Sociale en Aunis | La Rochelle" />
      <meta key="og:site_name" property="og:site_name" content="OUAAA! : Agir pour la Transition Ecologique & Sociale en Aunis | La Rochelle" />
      <meta key="og:url" property="og:url" content="https://ouaaa-transition.fr/" />
      <meta key="og:image" property="og:image" content="https://ouaaa-transition.fr//apple-touch-icon.png" />
      <meta key="twitter:url" name="twitter:url" content="https://ouaaa-transition.fr/" />
      <meta key="twitter:title" name="twitter:title" content="OUAAA!" />
      <meta key="twitter:description" name="twitter:description" content="OUAAA! : Agir pour la Transition Ecologique & Sociale en Aunis | La Rochelle" />
      <meta key="twitter:image" name="twitter:image" content="https://ouaaa-transition.fr//apple-touch-icon.png" />
      <meta name="application-name" content="OUAAA!" />
      <meta name="apple-mobile-web-app-title" content="OUAAA! : Agir pour la Transition Ecologique & Sociale en Aunis | La Rochelle" />

    </Head>
    <Script src="/analytics/matomo.js" strategy="afterInteractive" />
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <MuiThemeProvider theme={theme}>
        <SessionProvider init={user}>
          {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
          <CssBaseline />
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
          >
            <Sentry.ErrorBoundary
              fallback={({ resetError }) => (
                <div style={{ padding: '2em', textAlign: 'center' }}>
                  <h2>Une erreur est survenue</h2>
                  <p>Nous avons été notifiés et travaillons à la résoudre.</p>
                  <button onClick={() => { resetError(); if (typeof window !== 'undefined') window.location.reload(); }}>
                    Recharger la page
                  </button>
                </div>
              )}
            >
              <Component {...pageProps} />
            </Sentry.ErrorBoundary>
            {!hideChatbot && <ChatbotWidget />}
          </SnackbarProvider>
        </SessionProvider>
      </MuiThemeProvider>
    </LocalizationProvider>
  </CacheProvider></ApolloProvider>;
};

MyApp.getInitialProps = async (ctx: AppContext) => {
  let user: UserData | null = null;

  if (typeof window === 'undefined') {
    const { initOnContext } = await import('hoc/withApollo');
    const apolloClientCtx = initOnContext(ctx);
    const result = await apolloClientCtx.ctx.apolloClient.query({
      query: ISLOGGED,
    });
    if (result.data?.isLogged?.id) {
      user = omitTypename(result.data.isLogged);
    }
  }

  if (user) {
    return { user };
  }
  return {};
};

export default MyApp;
